import type { Schema } from '@dealdesk/api-schema';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { STALE } from '@/shared/api/stale';

export interface UserParams {
  q?: string | undefined;
  role?: string | undefined;
  team?: string | undefined;
  status?: string | undefined;
  employment?: string | undefined;
}

export interface AuditParams {
  q?: string | undefined;
  entity?: string | undefined;
  page?: number | undefined;
  size?: number | undefined;
}

export const adminKeys = {
  all: ['admin'] as const,
  usersAll: ['admin', 'users'] as const,
  users: (p: UserParams) => ['admin', 'users', p] as const,
  roles: ['admin', 'roles'] as const,
  role: (id: string) => ['admin', 'roles', 'detail', id] as const,
  teams: ['admin', 'teams'] as const,
  audit: (p: AuditParams) => ['admin', 'audit', p] as const,
  org: ['admin', 'org'] as const,
  units: ['admin', 'org', 'units'] as const,
  structure: (asAt: string | undefined) => ['admin', 'org', 'structure', asAt ?? 'today'] as const,
  memberships: (userId: string) => ['admin', 'org', 'memberships', userId] as const,
  parties: ['admin', 'external-parties'] as const,
  employee: (userId: string) => ['admin', 'users', 'employee', userId] as const,
};

const defined = (o: Record<string, string | undefined>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v)) as Record<string, string>;

/** Users list; the server filters, the workspace pages on the client (the list is one organisation's staff). */
export const usersQuery = (p: UserParams) =>
  queryOptions({
    queryKey: adminKeys.users(p),
    queryFn: ({ signal }) => unwrap(api.GET('/admin/users', { params: { query: defined({ q: p.q, role: p.role, team: p.team, status: p.status, employment: p.employment }) }, signal })),
    staleTime: STALE.list,
    placeholderData: keepPreviousData,
  });

export const rolesQuery = queryOptions({
  queryKey: adminKeys.roles,
  queryFn: ({ signal }) => unwrap(api.GET('/admin/roles', { signal })),
  staleTime: STALE.list,
});

export const roleQuery = (roleId: string) =>
  queryOptions({
    queryKey: adminKeys.role(roleId),
    queryFn: ({ signal }) => unwrap(api.GET('/admin/roles/{roleId}', { params: { path: { roleId } }, signal })),
    staleTime: STALE.record,
  });

export const teamsQuery = queryOptions({
  queryKey: adminKeys.teams,
  queryFn: ({ signal }) => unwrap(api.GET('/admin/teams', { signal })),
  staleTime: STALE.reference,
});

export const auditQuery = (p: AuditParams) =>
  queryOptions({
    queryKey: adminKeys.audit(p),
    queryFn: ({ signal }) => unwrap(api.GET('/admin/audit-log', { params: { query: { ...(p.q ? { q: p.q } : {}), ...(p.entity ? { entity: p.entity } : {}), ...(p.page ? { page: p.page } : {}), ...(p.size ? { size: p.size } : {}) } }, signal })),
    staleTime: STALE.list,
    placeholderData: keepPreviousData,
  });

export const unitsQuery = queryOptions({
  queryKey: adminKeys.units,
  queryFn: ({ signal }) => unwrap(api.GET('/org/units', { signal })),
  staleTime: STALE.reference,
});

/** The organisation as at a date (people placed by their effective-dated memberships). */
export const structureQuery = (asAt: string | undefined) =>
  queryOptions({
    queryKey: adminKeys.structure(asAt),
    queryFn: ({ signal }) => unwrap(api.GET('/org/structure', { params: { query: asAt ? { asAt } : {} }, signal })),
    staleTime: STALE.reference,
    placeholderData: keepPreviousData,
  });

export const membershipsQuery = (userId: string) =>
  queryOptions({
    queryKey: adminKeys.memberships(userId),
    queryFn: ({ signal }) => unwrap(api.GET('/org/memberships', { params: { query: { userId } }, signal })),
    staleTime: STALE.record,
  });

export const partiesQuery = queryOptions({
  queryKey: adminKeys.parties,
  queryFn: ({ signal }) => unwrap(api.GET('/external-parties', { signal })),
  staleTime: STALE.reference,
});

/** Employee number and bank-issued codes of a user (admin.users or org.manage). */
export const employeeDetailsQuery = (userId: string) =>
  queryOptions({
    queryKey: adminKeys.employee(userId),
    queryFn: ({ signal }) => unwrap(api.GET('/admin/users/{userId}/employee-details', { params: { path: { userId } }, signal })),
    staleTime: STALE.record,
  });

export type AdminUser = Schema<'AdminUser'>;
export type Role = Schema<'Role'>;
